// Rhythms on a grid, for Grow's techniques: a voice as hits at tick positions (24 to a quarter
// note, so 16ths, 32nds, 8th-note triplets and sextuplets all land on whole ticks), and back to
// notes, rests and tuplets one beat at a time. TrueChops has no ties, so a hit lasts at most to
// the end of its beat; drums don't sustain, so the rest of the beat is written as rests.
import { noteQuarterDuration } from "../book-practice-score";

export const TICKS = 24;
// Written note values: [ticks, duration, dots], longest first.
const VALUES = [[96, 1, 0], [72, 2, 1], [48, 2, 0], [36, 4, 1], [24, 4, 0], [18, 8, 1], [12, 8, 0], [9, 16, 1], [6, 16, 0], [3, 32, 0]];

export const measureTicks = (timeSig) => Math.round(timeSig.num * 4 / timeSig.type * TICKS);
const compound = (timeSig) => timeSig.type === 8 && timeSig.num % 3 === 0 && timeSig.num >= 6;
export function beatTicks(timeSig) {
  if (compound(timeSig)) return 36;
  return timeSig.type >= 16 ? 6 : timeSig.type === 8 ? 12 : TICKS;
}
// How a beat can be divided, simplest first: the step between grid lines, how each step is
// written, and the tuplet when it's one.
function gridsFor(beat) {
  if (beat === 36) return [{ step: 12, written: 12 }, { step: 6, written: 6 }, { step: 3, written: 3 }];
  if (beat === 12) return [{ step: 6, written: 6 }, { step: 3, written: 3 }];
  if (beat === 6) return [{ step: 6, written: 6 }, { step: 3, written: 3 }];
  return [{ step: 6, written: 6 }, { step: 8, written: 12, tuplet: { actual: 3, normal: 2 } }, { step: 4, written: 6, tuplet: { actual: 6, normal: 4 } }, { step: 3, written: 3 }];
}

// The notes a voice sounds as hits ({ at, notes, ornaments, velocity, dynamic?, head? }), or
// null when a note falls between ticks (quintuplets and the like), which grid edits leave alone.
export function hitsOf(voice) {
  const hits = [];
  let at = 0;
  for (const [index, note] of voice.notes.entries()) {
    if (Math.abs(at - Math.round(at)) > 1e-6) return null;
    if (note.notes?.length) hits.push({ at: Math.round(at), notes: [...note.notes], ornaments: note.ornaments || "", velocity: note.velocity ?? 0.5,
      ...(note.dynamic ? { dynamic: note.dynamic } : {}), ...(note.head ? { head: note.head } : {}) });
    at += noteQuarterDuration(note, index, voice.tuplets || []) * TICKS;
  }
  return Math.abs(at - Math.round(at)) > 1e-6 ? null : hits;
}

// The longest written values adding up to `ticks`.
function valuesFor(ticks) {
  const parts = [];
  let left = ticks;
  while (left > 0) {
    const value = VALUES.find(([length]) => length <= left);
    if (!value) break;
    parts.push(value);
    left -= value[0];
  }
  return parts;
}
const rest = ([, duration, dots]) => ({ notes: [], duration, dots, ornaments: "", velocity: 0.5 });
const sounding = (hit, [, duration, dots]) => ({ notes: hit.notes, duration, dots, ornaments: hit.ornaments || "", velocity: hit.velocity ?? 0.5,
  ...(hit.dynamic ? { dynamic: hit.dynamic } : {}), ...(hit.head ? { head: hit.head } : {}) });

// One beat's notes (and its tuplet, if it needs one). `hits` are within the beat, `at` from its start.
function writeBeat(hits, beat) {
  const grids = gridsFor(beat);
  let grid = grids.find((option) => hits.every((hit) => hit.at % option.step === 0));
  if (!grid) {
    grid = grids[0];
    hits = mergeHits(hits.map((hit) => ({ ...hit, at: Math.min(beat - grid.step, Math.round(hit.at / grid.step) * grid.step) })));
  }
  const scale = grid.tuplet ? grid.tuplet.actual / grid.tuplet.normal : 1;
  const notes = [], written = (ticks) => Math.round(ticks * scale);
  if (!hits.length) return { notes: valuesFor(beat).map(rest), tuplet: null };
  for (const value of valuesFor(written(hits[0].at))) notes.push(rest(value));
  hits.forEach((hit, index) => {
    const span = written((index + 1 < hits.length ? hits[index + 1].at : beat) - hit.at);
    const exact = VALUES.find(([length]) => length === span);
    const [first, ...others] = exact ? [exact] : valuesFor(span);
    notes.push(sounding(hit, first));
    for (const value of others) notes.push(rest(value));
  });
  return { notes, tuplet: grid.tuplet && hits.length ? grid.tuplet : null };
}

// Hits at the same tick become one (their pitches together).
export function mergeHits(hits) {
  const byTick = new Map();
  for (const hit of [...hits].sort((a, b) => a.at - b.at)) {
    const previous = byTick.get(hit.at);
    if (previous) previous.notes = [...new Set([...previous.notes, ...hit.notes])];
    else byTick.set(hit.at, { ...hit, notes: [...hit.notes] });
  }
  return [...byTick.values()].filter((hit) => hit.notes.length);
}

// A voice ({ notes, tuplets }) from hits across a measure.
export function writeVoice(hits, timeSig) {
  const total = measureTicks(timeSig), beat = beatTicks(timeSig), merged = mergeHits(hits.filter((hit) => hit.at >= 0 && hit.at < total));
  const notes = [], tuplets = [];
  for (let start = 0; start < total; start += beat) {
    const length = Math.min(beat, total - start);
    const inBeat = merged.filter((hit) => hit.at >= start && hit.at < start + length).map((hit) => ({ ...hit, at: hit.at - start }));
    const { notes: written, tuplet } = length === beat ? writeBeat(inBeat, beat) : writeBeat(inBeat, length);
    if (tuplet) tuplets.push({ ...tuplet, start: notes.length, end: notes.length + written.length });
    notes.push(...written);
  }
  return { notes, tuplets };
}

// The grid step a beat's hits are on (6 for 16ths, 8 for 8th-note triplets, 4 for sextuplets…).
export function stepOf(hits, start, timeSig) {
  const beat = beatTicks(timeSig), inBeat = hits.filter((hit) => hit.at >= start && hit.at < start + beat);
  return (gridsFor(beat).find((option) => inBeat.every((hit) => (hit.at - start) % option.step === 0)) || gridsFor(beat)[0]).step;
}
